import { Button } from '@affine/component/ui/button';
import { useI18n } from '@affine/i18n';
import { useTheme } from 'next-themes';
import { type ReactNode, useCallback } from 'react';

import dotBgDark from './assets/dot-bg.dark.png';
import dotBgLight from './assets/dot-bg.light.png';
import { DesktopNavbar } from './desktop-navbar';
import * as styles from './index.css';
import { MobileNavbar } from './mobile-navbar';

export const AffineOtherPageLayout = ({
  children,
}: {
  children: ReactNode;
}) => {
  const t = useI18n();

  const openDownloadLink = useCallback(() => {
    open(BUILD_CONFIG.downloadUrl, '_blank');
  }, []);

  const { resolvedTheme } = useTheme();
  const backgroundImage =
    resolvedTheme === 'dark' && dotBgDark ? dotBgDark : dotBgLight;

  return (
    <div
      className={styles.root}
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      {BUILD_CONFIG.isElectron ? (
        <div className={styles.draggableHeader} />
      ) : (
        <div className={styles.topNav}>
          <a href="/" rel="noreferrer" className={styles.affineLogo}>
            {/* Inline SVG so currentColor adapts to light/dark nav */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 4068.06 881.05"
              width={120}
              height={24}
              aria-label="Tynk Tasks"
              style={{ display: 'block' }}
            >
              <path
                d="M1007.1,685.84l-163.6.16-1.19-451.27-265.04,646.32-163.68-.42,98.99-218.49-144.97-360.38-2.21,60.6-101.03.18-.1,323.44H100.97s-.12-323.43-.12-323.43l-100.77-.24-.08-135.98,100.77-.27.4-189.66h162.98s.15,189.69.15,189.69l248.11.23,79.15,263.19,79.37-263.24,335.43.06,1.54,29.29c34.8-26.05,75.18-37.47,118.48-39.07,98.34-3.62,179.89,73.33,178.78,172.21l-.06,297.25h-163.27s-.52-263.09-.52-263.09c-.43-39.88-30.42-65.96-68.74-64.99-35.24.9-62.23,23.27-65.15,59.79l-.34,268.11Z"
                fill="currentColor"
              />
              <polygon
                points="1670.91 441.18 1835.62 685.8 1648.46 686.12 1516.9 467.73 1516.57 686.06 1352.82 685.94 1352.89 .29 1516.5 0 1516.78 424.88 1647.13 226.17 1825.62 226.94 1670.91 441.18"
                fill="currentColor"
              />
              <polygon
                points="3505.63 456.77 3734.94 684.86 3649.17 685.42 3430.43 466.96 3429.44 685.25 3363.91 685.61 3363.86 21.13 3429.62 20.57 3429.96 452.19 3645.28 244.36 3725.81 244.75 3505.63 456.77"
                fill="#738803"
              />
              <path
                d="M3181.52,687.18c-84.34,22.19-179.42-2.37-236.48-71.78l42.32-43.28c31.35,39.87,76.63,62.14,127.21,62.79,31.49.4,74.93-3.59,92.96-34.02,12.08-20.39,10.82-48.48-4-67.18-42.17-53.2-178.83-41.97-221.1-121.3-9.25-17.36-11.34-36.41-11.31-56.04.09-66.7,50.84-108.63,113.02-118.46,69.71-11.02,150.74,5.69,194.42,66.93l-42.2,42.43c-26.55-33.45-65.56-50.9-107.69-52.49-21.71-.82-42.77.81-61.9,9.99-16.18,7.77-28.2,21.98-31.09,36.65-3.85,19.55.29,38.61,14.39,51.29,13.26,11.93,28.99,19.54,45.85,24.72l63.94,19.64c61.71,18.96,117.32,48.18,118.87,121.29.63,29.88-5.03,58.53-23.9,82.15-18.65,23.34-44,38.95-73.31,46.66Z"
                fill="#738803"
              />
              <path
                d="M3909.42,694.94c-68.32.11-131.4-26.93-175.29-79.36l42-43.34c19.98,23.22,42.01,41.95,70.64,52.07,42.76,15.11,120.87,19.46,148.41-21.38,13.88-20.58,12.55-50.54-3.12-69.77-42.7-52.39-186.12-44.09-221.59-123.02-15.11-33.62-14.41-73.87,2.45-106.75,37.87-73.86,147.36-80.29,216.82-56.17,31.26,11.94,58.05,29.82,77.69,57.66l-41.87,42.24c-16.81-20.08-36.79-35.05-61.86-43.65-38.55-13.22-108.31-16.55-132.81,23.26-12,19.49-10.07,46.73,5.76,63.39,12.16,12.8,27.79,20.75,44.26,25.85l66.11,20.48c64.03,19.84,117.06,46.93,120.86,120.87,2.61,50.67-20.69,95.16-66.76,118.12-27.95,13.93-58.12,19.45-91.68,19.51Z"
                fill="#738803"
              />
              <polygon
                points="2287.67 685.12 2221.71 685.44 2221.61 303.68 2110.26 302.93 2110.28 244.21 2221.41 243.87 2221.77 58.19 2287.63 58.32 2287.7 243.71 2398.39 244.25 2398.43 302.95 2287.73 303.28 2287.67 685.12"
                fill="#738803"
              />
              <path
                d="M2063.1,643.21c-1.37,30.94-25.14,52.18-53.48,51.84s-51.75-23.36-52.28-53.12c-.54-30.28,22.3-54.33,53.13-54.15s54.03,23.7,52.62,55.43Z"
                fill="#738803"
              />
              <path
                d="M2804.39,244.26l-.99,71.79c-23.52-34.38-55.44-59.85-96.16-72-113.43-33.85-226.99,25.45-267.35,136.01-19.87,54.43-19.99,114.98-.58,169.56,38.79,109.07,151.99,168.08,263.75,137.38,42.26-11.61,76.63-37.64,100.62-74.93l.93,73.81,65.5-.12V244.43s-65.71-.18-65.71-.18ZM2792.5,545.1c-23.99,56.59-74.92,86.83-133.88,87.97-72.2,1.4-135-40.62-156.94-110.42-11.98-38.1-11.85-79.86.66-117.83,22.5-68.31,85.06-109.95,156.27-108.19,61.87,1.53,115.2,36.53,136.73,95.71,17.62,48.44,17.73,104.25-2.83,152.76Z"
                fill="#738803"
              />
            </svg>
          </a>

          <DesktopNavbar />
          <Button
            onClick={openDownloadLink}
            className={styles.hideInSmallScreen}
          >
            {t['com.affine.auth.open.affine.download-app']()}
          </Button>
          <MobileNavbar />
        </div>
      )}

      {children}
    </div>
  );
};
